import type { UseQueryResult } from "@tanstack/react-query";

/**
 * The app chains queries with `enabled: !!dependency`. React Query reports a
 * disabled query as `pending` with `isFetching: false`, which is
 * indistinguishable from a query that is genuinely in flight — so when the
 * dependency fails, every consumer that renders on `isLoading` / `!isFetched`
 * shows a loading skeleton forever.
 *
 * These helpers fold the dependency's outcome into the dependent's, so a
 * dependency that failed (or resolved to nothing) surfaces as an error rather
 * than as perpetual loading.
 */

/** The subset of `UseQueryResult` the combination depends on. */
export interface QueryStateInput<TData = unknown> {
  data: TData | undefined;
  error: unknown;
  isError: boolean;
  isSuccess: boolean;
  isFetching: boolean;
}

export interface CombinedQueryState {
  status: "pending" | "error" | "success";
  isLoading: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  isFetched: boolean;
  error: unknown;
}

export interface CombineOptions {
  /**
   * Treat a dependency that resolved to `null`/`undefined` as a failure. The
   * dependent query is gated on that value, so it can never run — reporting it
   * as loading would hang the UI just as a rejection does.
   */
  requireDependencyValue?: boolean;
  /** Message for the synthetic error raised by `requireDependencyValue`. */
  missingDependencyMessage?: string;
}

export class MissingDependencyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MissingDependencyError";
  }
}

export function combineDependentQueryState(
  dependency: QueryStateInput,
  dependent: QueryStateInput,
  options: CombineOptions = {},
): CombinedQueryState {
  const {
    requireDependencyValue = false,
    missingDependencyMessage = "Required data was not found",
  } = options;

  // Once the dependent has its own data, the dependency's outcome no longer
  // determines what we can render — a failed background refresh of the
  // dependency must not blank out a page that is already usable.
  if (!dependent.isSuccess) {
    if (dependency.isError) {
      return errorState(dependency.error);
    }

    if (
      requireDependencyValue &&
      dependency.isSuccess &&
      dependency.data == null
    ) {
      return errorState(new MissingDependencyError(missingDependencyMessage));
    }

    if (!dependency.isSuccess) {
      // The dependency has not resolved, so the dependent is still disabled.
      // Report the dependency's fetch as the loading state.
      return {
        status: "pending",
        isLoading: true,
        isPending: true,
        isError: false,
        isSuccess: false,
        isFetched: false,
        error: null,
      };
    }
  }

  if (dependent.isError) {
    return errorState(dependent.error);
  }

  if (dependent.isSuccess) {
    return {
      status: "success",
      isLoading: false,
      isPending: false,
      isError: false,
      isSuccess: true,
      isFetched: true,
      error: null,
    };
  }

  return {
    status: "pending",
    isLoading: true,
    isPending: true,
    isError: false,
    isSuccess: false,
    isFetched: false,
    error: null,
  };
}

function errorState(error: unknown): CombinedQueryState {
  return {
    status: "error",
    isLoading: false,
    isPending: false,
    isError: true,
    isSuccess: false,
    // The chain has settled: consumers waiting on `isFetched` must stop waiting.
    isFetched: true,
    error,
  };
}

/**
 * Wraps a dependency/dependent query pair as a single `UseQueryResult`, so
 * existing consumers keep working while gaining a real error state.
 */
export function combineDependentQuery<
  TDependency,
  TData,
  TDependencyError = Error,
  TError = Error,
>(
  dependency: UseQueryResult<TDependency, TDependencyError>,
  dependent: UseQueryResult<TData, TError>,
  options: CombineOptions = {},
): UseQueryResult<TData, TError> {
  const state = combineDependentQueryState(dependency, dependent, options);

  const refetch: UseQueryResult<TData, TError>["refetch"] = async (
    refetchOptions,
  ) => {
    // The dependent is disabled while the dependency is failing, so refetching
    // it alone is a no-op. Re-run the dependency first; a success re-enables
    // the dependent, which then fetches on its own.
    if (dependency.isError) {
      await dependency.refetch(refetchOptions);
    }
    return dependent.refetch(refetchOptions);
  };

  return {
    ...dependent,
    ...state,
    refetch,
  } as UseQueryResult<TData, TError>;
}
